import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Fade,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import BpCheckbox from '../../../../../components/Checkout/BpCheckbox'
import {
  TPaymentMethodEnum,
  TProductDelivery,
  TProductFrequency,
  TProductPriceInstallmentFees,
} from '../../../../../core/types/Product'
import { ProductPriceDetailPaymentsProps } from './ProductPriceTypes'
import { useContext, useState } from 'react'
import { AuthContext } from '../../../../../core/context/AuthContext'
import NumberFunctions from '../../../../../core/functions/NumberFunctions'
import SwipeableDrawerRightDialog from '../../../../../components/Dialogs/SwipeableDrawerRightDialog'
import { useProductInstallments } from '../../../../../core/hooks/useProductInstallments'
import InfoTooltip from '../../../../../components/Tooltip/InfoTooltip'
import ProductFunctions from '../../../../../core/functions/ProductFunctions'

const ProductPriceDetailPayments = ({
  methodTypes,
  price,
  errorPayments,
  options,
  currency,
  delivery,
  setPrice,
  onChangeMethodTypes,
}: ProductPriceDetailPaymentsProps) => {
  const { isUserSysAdmin } = useContext(AuthContext)

  const [open, setOpen] = useState(false)

  const isInstallmentFeesCustomer =
    price.installmentFeesType === TProductPriceInstallmentFees.Customer
  const isInstallmentFeesProducer =
    price.installmentFeesType === TProductPriceInstallmentFees.Producer
  const isInstallmentFeesHybrid =
    price.installmentFeesType === TProductPriceInstallmentFees.Hybrid

  const isValueMoreTen = Number(price.value) >= 10

  const getMethodTypeCreditCard = () => {
    return methodTypes
      .filter((x) => x.selected)
      .find((x) => x.id === TPaymentMethodEnum.CreditCard.toString())
  }

  const isMethodTypeCreditCard = () => {
    const creditCard = getMethodTypeCreditCard()
    if (creditCard) return true
    return false
  }

  const getMethodTypeCreditCardInstallment = () => {
    const creditCard = getMethodTypeCreditCard()
    if (creditCard) return creditCard.value
    return 1
  }

  const maxInstallmentForProducer = isInstallmentFeesCustomer
    ? 0
    : isInstallmentFeesProducer
      ? getMethodTypeCreditCardInstallment()
      : (price.endInstallmentFees ?? getMethodTypeCreditCardInstallment())

  const showInstallmentSimulator = getMethodTypeCreditCardInstallment() > 1

  const { data, loading } = useProductInstallments({
    maxInstallment: Math.max(...options),
    value: price.value ?? 0,
    maxInstallmentForProducer,
    enabled: open,
  })

  const isPaymentChecked = (methodType: TPaymentMethodEnum) => {
    return (
      methodTypes.find(
        (mt) => mt.selected && mt.id === methodType.toString()
      ) != undefined
    )
  }

  const handleChangePayment = (
    event: React.ChangeEvent<HTMLInputElement>,
    methodType: TPaymentMethodEnum
  ) => {
    const method = methodTypes.find((mt) => mt.id === methodType.toString())
    if (method) {
      method.selected = event.target.checked

      if (methodType === TPaymentMethodEnum.CreditCard && method.selected) {
        method.value = Math.max(...options)
        price.endInstallmentFees = method.value
      }

      // Google Pay / Apple Pay são subcategorias do cartão de crédito
      if (methodType === TPaymentMethodEnum.CreditCard && !method.selected) {
        methodTypes.forEach((mt) => {
          if (
            mt.id === TPaymentMethodEnum.GooglePay.toString() ||
            mt.id === TPaymentMethodEnum.ApplePay.toString()
          ) {
            mt.selected = false
          }
        })
      }

      onChangeMethodTypes([...methodTypes], price)
    }
  }

  const handleChangeMaxInstallments = (e: SelectChangeEvent<number>) => {
    const creditCard = getMethodTypeCreditCard()
    if (creditCard) {
      const endInstallmentFees =
        price.endInstallmentFees ?? getMethodTypeCreditCardInstallment()
      if (endInstallmentFees === creditCard.value) {
        price.endInstallmentFees = Number(e.target.value)
      }
      creditCard.value = Number(e.target.value)
      if (endInstallmentFees >= creditCard.value) {
        price.endInstallmentFees = creditCard.value
      }
      const newItems = methodTypes.filter(
        (x) => x.id !== TPaymentMethodEnum.CreditCard.toString()
      )
      onChangeMethodTypes([...newItems, { ...creditCard }], price)
    }
  }

  const getOptionDescription = (option: number) => {
    const endInstallmentFees =
      price.endInstallmentFees ?? getMethodTypeCreditCardInstallment()
    const isOptionProducer = option <= endInstallmentFees
    const isHybridAndOption = isInstallmentFeesHybrid && isOptionProducer

    return `${option}x ${isInstallmentFeesProducer || isHybridAndOption ? 'SEM juros para o cliente' : ''}`
  }

  const showTicketReservationNotice =
    ProductFunctions.shouldShowTicketReservationNotice(
      delivery,
      isPaymentChecked(TPaymentMethodEnum.Pix),
      isPaymentChecked(TPaymentMethodEnum.Boleto)
    )

  return (
    <>
      <Fade
        in={methodTypes.length > 0}
        timeout={500}
        unmountOnExit
        mountOnEnter
      >
        <Stack direction="column" spacing={1}>
          <Typography
            color="#343948"
            fontSize="16px"
            lineHeight="20px"
            fontWeight={700}
          >
            Formas de Pagamento
          </Typography>
          {/*<ProductPricePayments
          selectds={methodTypes}
          setSelectds={handleChangeMethodTypes}
          onPayments={onPayments}
          disabled={isUserSysAdmin()}
        />*/}

          <Stack direction="column" spacing={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {currency !== 'usd' && (
                <FormControlLabel
                  sx={{ width: { xs: '100%', sm: '50%' } }}
                  disabled={isUserSysAdmin()}
                  control={
                    <BpCheckbox
                      checked={isPaymentChecked(TPaymentMethodEnum.Pix)}
                      onChange={(e) =>
                        handleChangePayment(e, TPaymentMethodEnum.Pix)
                      }
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        lineHeight="18px"
                        fontSize={{ xs: '13px', sm: '14px' }}
                        variant="body2"
                        sx={{
                          color: '#5D5E61',
                        }}
                      >
                        PIX
                      </Typography>
                      <InfoTooltip title="Pagamento via PIX com validade de 1 dia." />
                    </Stack>
                  }
                />
              )}
              {currency !== 'usd' && (!price.frequency ||
                price.frequency === TProductFrequency.Yearly) && (
                <FormControlLabel
                  sx={{ width: { xs: '100%', sm: '50%' } }}
                  disabled={isUserSysAdmin()}
                  control={
                    <BpCheckbox
                      checked={isPaymentChecked(TPaymentMethodEnum.Boleto)}
                      onChange={(e) =>
                        handleChangePayment(e, TPaymentMethodEnum.Boleto)
                      }
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        lineHeight="18px"
                        fontSize={{ xs: '13px', sm: '14px' }}
                        variant="body2"
                        sx={{
                          color: '#5D5E61',
                        }}
                      >
                        Boleto
                      </Typography>
                      <InfoTooltip title="Pagamento via boleto com validade de 3 dias." />
                    </Stack>
                  }
                />
              )}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControlLabel
                sx={{ width: { xs: '100%', sm: '50%' } }}
                disabled={isUserSysAdmin() || !isMethodTypeCreditCard()}
                control={
                  <BpCheckbox
                    checked={isPaymentChecked(TPaymentMethodEnum.GooglePay)}
                    onChange={(e) =>
                      handleChangePayment(e, TPaymentMethodEnum.GooglePay)
                    }
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      lineHeight="18px"
                      fontSize={{ xs: '13px', sm: '14px' }}
                      variant="body2"
                      sx={{
                        color: '#5D5E61',
                      }}
                    >
                      Google Pay
                    </Typography>
                    <InfoTooltip title="Subcategoria de cartão de crédito: só pode ser habilitado com o cartão de crédito selecionado." />
                  </Stack>
                }
              />
              <FormControlLabel
                sx={{ width: { xs: '100%', sm: '50%' } }}
                disabled={isUserSysAdmin() || !isMethodTypeCreditCard()}
                control={
                  <BpCheckbox
                    checked={isPaymentChecked(TPaymentMethodEnum.ApplePay)}
                    onChange={(e) =>
                      handleChangePayment(e, TPaymentMethodEnum.ApplePay)
                    }
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      lineHeight="18px"
                      fontSize={{ xs: '13px', sm: '14px' }}
                      variant="body2"
                      sx={{
                        color: '#5D5E61',
                      }}
                    >
                      Apple Pay
                    </Typography>
                    <InfoTooltip title="Subcategoria de cartão de crédito: só pode ser habilitado com o cartão de crédito selecionado." />
                  </Stack>
                }
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControlLabel
                sx={{ width: { xs: '100%', sm: '50%' } }}
                disabled={isUserSysAdmin()}
                control={
                  <BpCheckbox
                    checked={isPaymentChecked(TPaymentMethodEnum.CreditCard)}
                    onChange={(e) =>
                      handleChangePayment(e, TPaymentMethodEnum.CreditCard)
                    }
                  />
                }
                label={
                  <Typography
                    lineHeight="18px"
                    fontSize={{ xs: '13px', sm: '14px' }}
                    variant="body2"
                    sx={{
                      color: '#5D5E61',
                    }}
                  >
                    Cartão de Crédito
                  </Typography>
                }
              />
              <Fade
                in={isMethodTypeCreditCard()}
                timeout={500}
                unmountOnExit
                mountOnEnter
              >
                <FormControl sx={{ width: { xs: '100%', sm: '50%' } }}>
                  <InputLabel id="select-label-fees">
                    Taxas do parcelamento
                  </InputLabel>
                  <Select
                    variant="outlined"
                    disabled={isUserSysAdmin() || price.id !== ''}
                    size="small"
                    labelId="select-label-fees"
                    label="Taxas do parcelamento"
                    value={price.installmentFeesType}
                    onChange={(e) => {
                      const { value } = e.target
                      const installmentFeesType =
                        value as TProductPriceInstallmentFees
                      const endInstallmentFees =
                        installmentFeesType ===
                        TProductPriceInstallmentFees.Producer
                          ? getMethodTypeCreditCardInstallment()
                          : undefined
                      setPrice({
                        ...price,
                        installmentFeesType,
                        endInstallmentFees,
                      })
                    }}
                  >
                    <MenuItem
                      value={Number(TProductPriceInstallmentFees.Customer)}
                    >
                      Repassar (seu cliente paga as taxas)
                    </MenuItem>
                    <MenuItem
                      value={Number(TProductPriceInstallmentFees.Producer)}
                    >
                      Absorver (você quem paga as taxas)
                    </MenuItem>
                    <MenuItem
                      value={Number(TProductPriceInstallmentFees.Hybrid)}
                    >
                      Híbrido (escolha quem paga as taxas)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Fade>
            </Stack>
          </Stack>

          {errorPayments !== '' && (
            <Typography variant="overline" color={'red'}>
              {errorPayments}
            </Typography>
          )}

          {showTicketReservationNotice && (
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              <AlertTitle sx={{ fontSize: 14, fontWeight: 700 }}>
                Atenção sobre ingressos com PIX e Boleto
              </AlertTitle>
              Para compras via PIX, o ingresso fica reservado por 1 dia
              aguardando a confirmação do pagamento. Para compras via Boleto,
              fica reservado por 3 dias.
              <br />
              <br />
              Durante esse período, o comprador não tem acesso ao ingresso. Após
              a confirmação do pagamento, o ingresso é liberado automaticamente.
              Se o prazo expirar sem pagamento, a reserva é cancelada.
            </Alert>
          )}

          <Fade
            in={isMethodTypeCreditCard()}
            timeout={500}
            unmountOnExit
            mountOnEnter
          >
            <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl fullWidth>
                  <InputLabel id="select-label">
                    Max. Parcelas Cartão de Crédito
                  </InputLabel>
                  <Select
                    variant="outlined"
                    disabled={isUserSysAdmin()}
                    size="small"
                    labelId="select-label"
                    label="Max. Parcelas Cartão de Crédito"
                    value={getMethodTypeCreditCardInstallment()}
                    onChange={handleChangeMaxInstallments}
                  >
                    {options?.map((option) => (
                      <MenuItem value={option}>
                        {getOptionDescription(option)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {showInstallmentSimulator && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ textTransform: 'none', width: 180 }}
                    onClick={() => setOpen(true)}
                    endIcon={
                      <img src="/assets/icons/infos/info.svg" alt="info" />
                    }
                  >
                    Parcelas
                  </Button>
                )}
              </Stack>
              {price.installmentFeesType ===
                TProductPriceInstallmentFees.Hybrid && (
                <Stack direction="row" spacing={1}>
                  <FormControl fullWidth>
                    <InputLabel id="select-label-fees">
                      Assumir juros de parcelamento até
                    </InputLabel>
                    <Select
                      variant="outlined"
                      disabled={isUserSysAdmin()}
                      size="small"
                      labelId="select-label-fees"
                      label="Assumir juros de parcelamento até"
                      value={
                        price.endInstallmentFees ??
                        getMethodTypeCreditCardInstallment()
                      }
                      onChange={(e) => {
                        setPrice({
                          ...price,
                          endInstallmentFees:
                            e.target.value === ''
                              ? undefined
                              : NumberFunctions.toNumber(e.target.value),
                        })
                      }}
                    >
                      {options
                        ?.filter(
                          (option) =>
                            option <= getMethodTypeCreditCardInstallment()
                        )
                        .map((option) => (
                          <MenuItem value={option}>{option}x</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}

              {isValueMoreTen && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControlLabel
                    sx={{ width: { xs: '100%', sm: '50%' } }}
                    disabled={isUserSysAdmin()}
                    control={
                      <BpCheckbox
                        checked={isPaymentChecked(
                          TPaymentMethodEnum.TwoCreditCard
                        )}
                        onChange={(e) =>
                          handleChangePayment(
                            e,
                            TPaymentMethodEnum.TwoCreditCard
                          )
                        }
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          lineHeight="18px"
                          fontSize={{ xs: '13px', sm: '14px' }}
                          variant="body2"
                          sx={{
                            color: '#5D5E61',
                          }}
                        >
                          2 Cartões de Crédito
                        </Typography>
                        <InfoTooltip title="Pagamento via dois cartões de créditos." />
                      </Stack>
                    }
                  />
                  {/*<FormControlLabel
                    sx={{ width: { xs: '100%', sm: '50%' } }}
                    disabled={isUserSysAdmin()}
                    control={
                      <BpCheckbox
                        checked={isPaymentChecked(
                          TPaymentMethodEnum.CreditCardAndPix
                        )}
                        onChange={(e) =>
                          handleChangePayment(
                            e,
                            TPaymentMethodEnum.CreditCardAndPix
                          )
                        }
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          lineHeight="18px"
                          fontSize={{ xs: '13px', sm: '14px' }}
                          variant="body2"
                          sx={{
                            color: '#5D5E61',
                          }}
                        >
                          Cartão de Crédito e PIX
                        </Typography>
                        <InfoTooltip title="Pagamento via cartão de crédito e PIX." />
                      </Stack>
                    }
                  />*/}
                </Stack>
              )}
            </Stack>
          </Fade>
        </Stack>
      </Fade>

      <SwipeableDrawerRightDialog
        open={open}
        title="Valores do parcelamento"
        subtitle="Veja os valores de cada parcelamento"
        setOpen={setOpen}
        onClose={() => setOpen(false)}
      >
        <Stack sx={{ p: 0.5 }}>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={32} />
            </Stack>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 370 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Parcela</TableCell>
                    <TableCell>
                      {isInstallmentFeesProducer
                        ? 'Valor da Parcela (Sem juros para o Cliente)'
                        : 'Valor da Parcela (Juros repassados ao Cliente)'}
                    </TableCell>
                    <TableCell>
                      {isInstallmentFeesProducer
                        ? 'Valor Total Pago (Pelo Cliente)'
                        : 'Valor Total Pago (Juros repassados ao Cliente)'}
                    </TableCell>
                    <TableCell>
                      {isInstallmentFeesProducer
                        ? 'Recebimento do Produtor (Absorvendo os Juros)'
                        : 'Recebimento do Produtor'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data
                    ?.filter(
                      (d) =>
                        d.installmentNumber <=
                        getMethodTypeCreditCardInstallment()
                    )
                    .map((installment, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          backgroundColor:
                            index % 2 === 0 ? 'grey.100' : 'white',
                        }}
                      >
                        <TableCell component="th" scope="row">
                          <Typography variant="subtitle2">
                            {installment.installmentNumber}x
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {NumberFunctions.formatMoneyDefault(
                            installment.value
                          )}
                        </TableCell>
                        <TableCell>
                          {NumberFunctions.formatMoneyDefault(
                            installment.total
                          )}
                        </TableCell>
                        <TableCell>
                          {NumberFunctions.formatMoneyDefault(
                            installment.amount
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </SwipeableDrawerRightDialog>
    </>
  )
}

export default ProductPriceDetailPayments
